import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ColorRing } from "react-loader-spinner";

const TransferHistoryTable = () => {
  const [transferHistory, setTransferHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTransferHistory();
  }, []);

  const fetchTransferHistory = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrl("/api/warehouse/transfer/transferhisotry/gethistory"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (data?.data && Array.isArray(data.data)) {
        setTransferHistory(data.data);
      } else {
        setError("Invalid response format. Expected an array.");
      }
    } catch (error) {
      setError("An error occurred while fetching transfer history.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredHistory = transferHistory.filter((transfer) =>
    [
      transfer.fromWarehouse,
      transfer.toWarehouse,
      transfer.vehicleNumber,
      transfer.driverName,
      transfer.driverMobileNumber,
      transfer.transferDate,
    ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredHistory.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="bg-white">
      <div className="">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search Transfer History..."
            className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-700"
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white sticky top-0 z-10">
                <tr>
                  <th className="p-2 text-left font-normal tracking-wide">#</th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    From Warehouse
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    To Warehouse
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Vehicle Number
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Driver Name
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Driver Mobile
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Transfer Date
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Transfer Items
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.map((transfer, index) => (
                  <tr
                    key={transfer._id}
                    className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                  >
                    <td className="px-2 py-0.5">
                      {indexOfFirstItem + index + 1}
                    </td>
                    <td className="px-2 py-0.5 font-semibold">
                      {transfer.fromWarehouse}
                    </td>
                    <td className="px-2 py-0.5">{transfer.toWarehouse}</td>
                    <td className="px-2 py-0.5">{transfer.vehicleNumber}</td>
                    <td className="px-2 py-0.5">{transfer.driverName}</td>
                    <td className="px-2 py-0.5">
                      {transfer.driverMobileNumber}
                    </td>
                    <td className="px-2 py-0.5">
                      {new Date(transfer.transferDate).toLocaleDateString(
                        "en-IN",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        }
                      )}
                    </td>
                    <td className="px-2 py-0.5">
                      <ul>
                        {transfer.transferItems.map((item, idx) => (
                          <li key={idx}>
                            {item.itemName} ({item.itemCode}) -{" "}
                            {item.transferQuantity} units
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, filteredHistory.length)} of{" "}
            {filteredHistory.length} transfers
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg disabled:bg-gray-300 disabled:text-gray-400"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg disabled:bg-gray-300 disabled:text-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default React.memo(TransferHistoryTable);
